/**
 * decodeAudioBlob
 *
 * Decodes an audio blob to an AudioBuffer, or indicates that ogv.js
 * fallback is needed for browsers that don't support webm/opus natively (Safari).
 *
 * Returns: { audioBuffer, needsOgvPlayer }
 * - audioBuffer: decoded AudioBuffer (null if ogv player is needed)
 * - needsOgvPlayer: true if native decoding not supported and ogv.js should be used
 */

// Check if browser can decode webm audio natively
const canDecodeWebmNatively = () => {
  try {
    const audio = new Audio();
    const canPlay = audio.canPlayType('audio/webm; codecs=opus');
    return (canPlay === 'probably') || (canPlay === 'maybe');
  } catch {
    return false;
  }
};

// Cache the result since it won't change during session
let cachedCanDecodeWebm = null;
const canDecodeWebm = () => {
  if (cachedCanDecodeWebm === null) {
    cachedCanDecodeWebm = canDecodeWebmNatively();
  }
  return cachedCanDecodeWebm;
};

/**
 * Check if a blob needs ogv.js for playback
 * @param {Blob} blob - Audio blob to check
 * @returns {boolean} - true if ogv.js is needed
 */
export const needsOgvForPlayback = (blob) => {
  const isWebm = blob.type.includes('webm');
  return isWebm && (!canDecodeWebm());
};

/**
 * Decode audio blob to AudioBuffer
 * @param {Blob} blob - Audio blob to decode
 * @param {AudioContext} audioContext - Web Audio API context
 * @returns {Promise<{audioBuffer: AudioBuffer|null, needsOgvPlayer: boolean}>}
 */
const decodeAudioBlob = async (blob, audioContext) => {
  const isWebm = blob.type.includes('webm');
  const isOgg = blob.type.includes('ogg');

  // Check if we need ogv.js fallback
  if (needsOgvForPlayback(blob)) {
    return {
      audioBuffer: null,
      needsOgvPlayer: true,
    };
  }

  // Native decoding path
  try {
    const arrayBuffer = await blob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    return {
      audioBuffer,
      needsOgvPlayer: false,
    };
  } catch {
    // If native decoding fails, check if ogv.js can help
    // ogv.js only supports webm and ogg containers - not mp4
    const canUseOgvFallback = isWebm || isOgg;

    return {
      audioBuffer: null,
      needsOgvPlayer: canUseOgvFallback,
    };
  }
};

export default decodeAudioBlob;
